import {
    Form,
    Checkbox,
    Divider,
    Button,
    Space,
    Input,
    Spin,
    Drawer,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePermission, { setPackagePermission } from '../hooks/usePermission';
import useQuery from '../../../../util/useQuery';
import ModalDiscardChanges from '../../../../components/Shared/modal/discardChanges';
import { useBlocker } from '../../../../util/usePrompt';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ModalDelete from '../../../../components/Shared/modal/delete';
import {
    ClockCircleOutlined,
    CloseOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import History from './History';
import PermissionService from '../../../../services/Permission.service';

export default function Access({
    getPermissionList,
    isEdit,
    setIsEdit,
    permissionList,
}) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const params = useQuery();
    const navigate = useNavigate();

    const [errors, setErrors] = useState({
        message: '',
        error: false,
    });
    const [isDefault, setIsDefault] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    ///Discard change
    const [change, setChange] = useState(false);
    const [openChange, setOpenChange] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isChangeLocation, setIsChangeLocation] = useState(false);

    const [callHistory, setCallHistory] = useState(true);
    const [visible, setVisible] = useState(false);

    useBlocker((nextLocation) => onChangePage(nextLocation), change);

    const onChangePage = (nextLocation) => {
        setOpenChange(true);
        setIsChangeLocation(true);
        setNextLocation(nextLocation);
    };

    const handelOk = () => {
        setOpenChange(false);
        setChange(false);
        setIsEdit(false);
        onSetform();
    };

    const handelOkLocation = async () => {
        await setOpenChange(false);
        await setChange(false);
        await setIsChangeLocation(false);
        await navigate(
            nextLocation.location.pathname + nextLocation.location.search
        );
        setIsEdit(false);
    };

    const {
        loading,
        permissionDetail,
        getPermissionById,
        deletePermission,
        updatePermission,
    } = usePermission();

    useEffect(() => {
        if (params.get('id')) getPermissionById(params.get('id'));
    }, [getPermissionById, params]);

    const menuAccessPermissions = Form.useWatch('menuAccessPermissions', form);

    const handleSelectAll = (checked) => {
        const newData = menuAccessPermissions.map((item) => {
            const updatedSubMenuList = item.subMenuList.map((subItem) => ({
                ...subItem,
                permission: checked,
            }));
            return {
                ...item,
                selectAll: checked,
                subMenuList: updatedSubMenuList,
            };
        });

        form.setFieldsValue({
            menuAccessPermissions: newData,
        });
    };

    const handleCategoryselectAll = (index, checked) => {
        const newData = menuAccessPermissions.map((item, ind) => {
            if (ind === index) {
                return {
                    ...item,
                    selectAll: checked,
                    subMenuList: item.subMenuList.map((subItem) => ({
                        ...subItem,
                        permission: checked,
                    })),
                };
            }
            return item;
        });

        form.setFieldsValue({
            menuAccessPermissions: newData,
        });
    };

    const onSetform = useCallback(() => {
        if (permissionDetail?.permissionDetail) {
            const menuAccessPermissions = JSON.parse(
                permissionDetail?.permissionDetail
            );
            setPackagePermission(menuAccessPermissions);
            // Set packagePermission in subMenuList when undefined
            const initPackage = menuAccessPermissions?.map((item) => {
                const selectAll = item.subMenuList.every(
                    (subItem) =>
                        subItem.permission || !subItem.packagePermission
                );

                return {
                    ...item,
                    selectAll,
                    subMenuList: item.subMenuList
                        .map((subItem) => {
                            return {
                                ...subItem,
                                packagePermission:
                                    subItem.packagePermission === undefined
                                        ? true
                                        : subItem.packagePermission,
                            };
                        })
                        .filter((subItem) => !subItem.mainMenuName),
                };
            });
            const selectAll = initPackage.every(
                ({ selectAll, packagePermission }) =>
                    !packagePermission || selectAll
            );
            setIsDefault(
                permissionDetail?.permissionName?.includes('(Default)')
            );
            setIsSuperAdmin(
                permissionDetail?.permissionName?.includes('Super Admin')
            );

            form.setFieldsValue({
                selectAll,
                permissionName: permissionDetail?.permissionName,
                menuAccessPermissions: initPackage,
            });
        }
    }, [form, permissionDetail]);

    useEffect(() => {
        onSetform();
    }, [onSetform]);

    useEffect(() => {
        if (!menuAccessPermissions) return;

        const checkSubMenuPermissions = (subMenuList) =>
            subMenuList.every(
                (subItem) => subItem.permission || !subItem.packagePermission
            );

        const checkSubMenuUncheckedAll = (subMenuList) =>
            subMenuList.every(
                (subItem) => !subItem.permission || !subItem.packagePermission
            );

        const checkSelectAll = () => {
            const allSelected = menuAccessPermissions.every(
                (item) =>
                    !item.packagePermission ||
                    (item.selectAll &&
                        checkSubMenuPermissions(item.subMenuList))
            );

            const updatedData = menuAccessPermissions.map((item) => ({
                ...item,
                selectAll: checkSubMenuPermissions(item.subMenuList),
                permission: !checkSubMenuUncheckedAll(item.subMenuList),
            }));

            form.setFieldsValue({
                selectAll: allSelected,
                menuAccessPermissions: updatedData,
            });
        };

        checkSelectAll();

        const isAnySubMenuChecked = menuAccessPermissions.some(
            ({ subMenuList }) =>
                subMenuList.some((subItem) => subItem.permission)
        );

        setErrors({
            message: isAnySubMenuChecked
                ? ''
                : t('Please select at least 1 item'),
            error: !isAnySubMenuChecked,
        });
    }, [form, menuAccessPermissions, t]);

    const onFinish = (values) => {
        if (errors.error) return;
        const payload = {
            permissionId: params.get('id'),
            permissionName:
                values.permissionName || permissionDetail?.permissionName,
            permissionDetail: JSON.stringify(values.menuAccessPermissions),
            sequence: permissionDetail?.sequence,
        };
        updatePermission(payload, () => {
            if (params.get('id')) getPermissionById(params.get('id'));
            getPermissionList();
            setIsEdit(false);
            setChange(false);
        });
    };

    const onCancel = () => {
        if (change) {
            setOpenChange(true);
            return;
        }

        form.resetFields();
        onSetform();
        setIsEdit(false);
    };

    const handleDelete = () => {
        deletePermission(params.get('id'), () => {
            getPermissionList();
            setOpenDelete(false);
        });
    };
 
    return (
        <Spin spinning={loading}>
            <Form
                form={form}
                onFinish={onFinish}
                onFieldsChange={(_, allFields) => {
                    if (allFields.length > 0) {
                        setChange(true);
                    }
                }}
                layout="vertical"
            >
                <Space className="px-6 pt-4 w-full justify-between">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-2xl text-purple-PHX">
                            {t('Menu access permissions')}
                        </div>
                        <Button
                            onClick={() => {
                                setCallHistory(true);
                                setVisible(true);
                            }}
                            className="border-none bg-[#F3F6F9] h-8 w-8 flex items-center justify-center rounded-lg text-purple text-sm ml-2"
                        >
                            <ClockCircleOutlined />
                        </Button>
                        <Drawer
                            width={460}
                            title={
                                <div className="flex justify-between items-center">
                                    <span className="text-purple text-[17px]">
                                        {t('History')}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={(e) => setVisible(false)}
                                        className="bg-[#F3F6F9] h-8 w-8 flex items-center justify-center rounded-lg text-purple text-sm font-semibold"
                                    >
                                        <CloseOutlined />
                                    </button>
                                </div>
                            }
                            placement="right"
                            onClose={() => setVisible(false)}
                            closable={false}
                            open={visible}
                            className="history"
                        >
                            <History
                                callHistory={callHistory}
                                setCallHistory={setCallHistory}
                                service={PermissionService.getHistory}
                            />
                        </Drawer>
                    </div>

                    {!isSuperAdmin && (
                        <>
                            {!isEdit ? (
                                <Space>
                                    <Button
                                        size="large"
                                        type="primary"
                                        className="px-10"
                                        onClick={() => setIsEdit(true)}
                                    >
                                        {t('Edit')}
                                    </Button>
                                    {!isDefault && (
                                        <Button
                                            size="large"
                                            type="ghost"
                                            icon={<DeleteOutlined />}
                                            className="border-[#F0536F] text-[#F0536F] hover:opacity-60 hover:border-[#F0536F] hover:text-[#F0536F] focus:border-[#F0536F] focus:text-[#F0536F]"
                                            onClick={() => setOpenDelete(true)}
                                        >
                                            {t('Delete')}
                                        </Button>
                                    )}
                                </Space>
                            ) : (
                                <Space>
                                    <Button
                                        onClick={onCancel}
                                        size="large"
                                        className="border-purple-PHX text-purple-PHX px-10"
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        htmlType="submit"
                                        size="large"
                                        type="primary"
                                        className="px-10"
                                    >
                                        {t('Save')}
                                    </Button>
                                </Space>
                            )}
                        </>
                    )}
                </Space>
                <Divider />
                <div className="px-6 pb-6">
                    {!isDefault && (
                        <Form.Item
                            label={t('Permission Name')}
                            name="permissionName"
                            rules={[
                                {
                                    required: true,
                                    message: t('Please input permission Name'),
                                },
                                {
                                    validator: (_, value) => {
                                        // Check if the permission name is already in the list\
                                        if (
                                            value ===
                                            permissionDetail?.permissionName
                                        ) {
                                            return Promise.resolve();
                                        }
                                        // Remove last whitespace value
                                        const isExist = permissionList.some(
                                            (item) =>
                                                item.permissionName === value
                                        );
                                        if (isExist) {
                                            return Promise.reject(
                                                new Error(
                                                    t(
                                                        'Permission name already exists'
                                                    )
                                                )
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                disabled={!isEdit}
                                size="large"
                                placeholder="Search"
                                className="w-full"
                                maxLength={100}
                                showCount
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        validateStatus={errors.error && 'error'}
                        help={isEdit && errors.error && errors.message}
                    >
                        <div className="font-semibold pb-4">
                            {isSuperAdmin
                                ? t(
                                      'Super Admin permissions cannot be modified'
                                  )
                                : t(
                                      'Please check the box if you want users to see the menu'
                                  )}
                        </div>
                    </Form.Item>

                    <Form.Item
                        name="selectAll"
                        valuePropName="checked"
                        className="mb-0"
                    >
                        <Checkbox
                            disabled={!isEdit}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="font-semibold"
                        >
                            {t('Select All')}
                        </Checkbox>
                    </Form.Item>

                    <Form.List
                        rules={[
                            {
                                required: true,
                                message: 'Please select permission',
                            },
                        ]}
                        name="menuAccessPermissions"
                    >
                        {() => (
                            <>
                                {menuAccessPermissions?.map(
                                    (
                                        {
                                            mainMenuName,
                                            subMenuList,
                                            packagePermission,
                                        },
                                        index
                                    ) => (
                                        <div key={mainMenuName}>
                                            <div
                                                hidden={!packagePermission}
                                                className="bg-[#F4F6FC] p-4 rounded-md flex justify-between items-center my-4"
                                            >
                                                <div className="font-semibold text-purple-PHX text-base">
                                                    {mainMenuName}
                                                </div>

                                                <Form.Item
                                                    name={[index, 'selectAll']}
                                                    valuePropName="checked"
                                                    className="mb-0"
                                                >
                                                    <Checkbox
                                                        disabled={!isEdit}
                                                        onChange={(e) =>
                                                            handleCategoryselectAll(
                                                                index,
                                                                e.target.checked
                                                            )
                                                        }
                                                        className="font-semibold text-purple-PHX"
                                                    >
                                                        {t('All')}
                                                    </Checkbox>
                                                </Form.Item>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                {subMenuList.map(
                                                    (
                                                        {
                                                            sequence,
                                                            subMenuName,
                                                            packagePermission:
                                                                subMenuPackagePermission,
                                                        },
                                                        ind
                                                    ) => (
                                                        <Form.Item
                                                            hidden={
                                                                !subMenuPackagePermission ||
                                                                !packagePermission
                                                            }
                                                            key={sequence}
                                                            name={[
                                                                index,
                                                                'subMenuList',
                                                                ind,
                                                                'permission',
                                                            ]}
                                                            valuePropName="checked"
                                                            className="mb-0"
                                                        >
                                                            <Checkbox
                                                                disabled={
                                                                    !isEdit
                                                                }
                                                            >
                                                                {subMenuName}
                                                            </Checkbox>
                                                        </Form.Item>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )
                                )}
                            </>
                        )}
                    </Form.List>
                </div>
            </Form>
            <ModalDelete
                loading={loading}
                open={openDelete}
                handelClose={() => {
                    setOpenDelete(false);
                }}
                handelOk={handleDelete}
            />
            <ModalDiscardChanges
                open={openChange}
                handelOk={isChangeLocation ? handelOkLocation : handelOk}
                handelClose={() => setOpenChange(false)}
            />
        </Spin>
    );
}

// Validate props
Access.propTypes = {
    getPermissionList: PropTypes.func,
    isEdit: PropTypes.bool,
    setIsEdit: PropTypes.func,
    permissionList: PropTypes.array,
};
